import { DestroyRef, Injectable } from '@angular/core';
import { SortedField, Sorting } from './sorting';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Filter, Filters } from './filter';
import { Query } from './query';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Define uma consulta simples, sem paginação.
 * Simplifica a realização de consultas a APIs que usam o padrão de consulta:
 *
 *    /endpoint?filter1=teste&filter2=teste2&sort=name,asc
 *
 * As consultas ao Endpoint são criadas dinamicamente, usando o padrão acima.
 *
 * @param T Tipo dos dados que serão retornados na consulta.
 */
export class ListQuery<T> implements Query<T[]> {
    total: number = 0;
    private loadingSubject = new BehaviorSubject<boolean>(false);
    public readonly loadingObservable = this.loadingSubject.asObservable();
    private sorting: Sorting;
    private filters: Filters = new Filters();
    private readonly dataSubject = new BehaviorSubject<T[]>([]);
    private readonly dataObservable = this.dataSubject.asObservable();
    private readonly endpoint: string;
    private autoRefresh: number | null = null;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
        readonly baseUrl: string,
        readonly path: string,
        readonly preFetch: boolean = false,
        sorting: Sorting = new Sorting(),
        filters: Filters = new Filters(),
        autoRefresh: number | null = null,
    ) {
        this.endpoint = `${baseUrl}/${path}`;
        this.sorting = sorting;
        this.filters = filters;
        this.baseUrl = baseUrl;
        this.autoRefresh = autoRefresh;

        if (preFetch) {
            this.fetch();
        }

        if (this.autoRefresh && this.autoRefresh >= 2000) {
            this.handleAutoRefresh();
        }
    }

    /**
     * Obtém um Observable que emite a página atual quando ela estiver pronta.
     *
     * @returns Observable que emite a página atual quando ela estiver pronta.
     */
    onReady(): Observable<T[]> {
        return this.dataObservable;
    }

    isLoading(): Observable<boolean> {
        return this.loadingObservable;
    }

    /**
     * Adiciona um campo de ordenação à consulta.
     *
     * @param field O campo a ser ordenado.
     * @param direction A direção da ordenação (asc ou desc).
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    sortBy(
        field: string,
        direction: 'asc' | 'desc' | '' | undefined,
    ): ListQuery<T> {
        this.sorting.addField(
            new SortedField(
                field,
                direction === 'asc' ? Sorting.ASC : Sorting.DESC,
            ),
        );
        return this;
    }

    /**
     * Adiciona um filtro à consulta.
     *
     * @param name O nome do filtro.
     * @param value O valor do filtro.
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    filterBy(name: string, value: any): ListQuery<T> {
        this.filters.add(name, value);
        return this;
    }

    /**
     * Limpa os campos de ordenação da consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearSorting(): ListQuery<T> {
        this.sorting = new Sorting();
        return this;
    }

    /**
     * Limpa os filtros aplicados na consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearFilters(): ListQuery<T> {
        this.filters = new Filters();
        return this;
    }

    /**
     * Realiza a busca dos dados na API.
     *
     * Caso uma requisição já esteja em andamento, a requisição
     * será ignorada para evitar múltiplas requisições simultâneas.
     *
     * @returns void
     */
    fetch(): void {
        // Evita múltiplas requisições simultâneas.
        if (this.loadingSubject.value) {
            return;
        }

        const filterQueryString = this.filters.toQueryString();
        const sortQueryString = this.sorting.toQueryString();
        this.loadingSubject.next(true);

        this.httpClient
            .get<T[]>(
                `${this.endpoint}/?per_page=9999&${filterQueryString}${sortQueryString ? `&${sortQueryString}` : ''}`,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (value: any) => {
                    let data: T[] = [];

                    if (Array.isArray(value)) {
                        data = value;
                    } else {
                        if (value.data) {
                            data = value.data;
                        }
                    }

                    this.total = data.length;
                    this.dataSubject.next(data);
                    this.loadingSubject.next(false);
                },
                error: (error: HttpErrorResponse) => {
                    console.error(error);
                    this.dataSubject.error(error);
                    this.loadingSubject.next(false);
                },
            });
    }

    /**
     * Trata o processo de autoRefresh, criando um interval com o RxJs.
     *
     * @private
     */
    private handleAutoRefresh(): void {
        // O timer recorrente não completa sozinho; encerra junto com o escopo
        // de injeção (destroyRef) para evitar vazamento de assinatura.
        timer(this.autoRefresh!, this.autoRefresh!)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    if (!this.isLoading()) {
                        this.fetch();
                    }
                },
            });
    }
}

/**
 * Builder para simplificar a criação de objetos ListQuery.
 *
 * Ao ser injetado em um componente, já leva junto o httpClient e destroyRef
 * necessários para o funcionamento do ListQuery criada.
 */
@Injectable({ providedIn: 'root' })
export class ListQueryBuilder {
    private aBaseUrl: string | null = null;
    private aPath: string | null = null;
    private aPreFetch: boolean = false;
    private aSortedFields: SortedField[] = [];
    private aFilters: Filter[] = [];

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
    ) {}

    /**
     * Define o caminho da API para a consulta paginada.
     *
     * @param path Caminho da API.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    path(path: string): ListQueryBuilder {
        this.aPath = path;
        return this;
    }

    /**
     * Define a URL base para a consulta paginada.
     *
     * @param baseUrl URL base da API.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    baseUrl(baseUrl: string): ListQueryBuilder {
        this.aBaseUrl = baseUrl;
        return this;
    }

    /**
     * Define se a primeira página deve ser pré-carregada.
     *
     * @param preFetch Indica se a primeira página deve ser pré-carregada.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    preFetch(preFetch: boolean): ListQueryBuilder {
        this.aPreFetch = preFetch;
        return this;
    }

    /**
     * Adiciona um campo para ordenação na consulta paginada.
     *
     * @param field Campo a ser ordenado.
     * @param direction Direção da ordenação (asc ou desc).
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    sortBy(field: string, direction: 'asc' | 'desc'): ListQueryBuilder {
        this.aSortedFields.push(new SortedField(field, direction));
        return this;
    }

    /**
     * Adiciona um filtro à consulta paginada.
     *
     * @param name Nome do filtro.
     * @param value Valor do filtro.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    filterBy(name: string, value: string): ListQueryBuilder {
        this.aFilters.push(new Filter(name, value));
        return this;
    }

    /**
     * Constrói e retorna uma instância de PagedQuery com as configurações definidas.
     *
     * @returns Instância de PagedQuery configurada.
     */
    build<T>(): ListQuery<T> {
        if (!this.aPath || !this.aBaseUrl) {
            throw new Error('Os parâmetros BaseUrl e Path são obrigatórios.');
        }

        const listQuery = new ListQuery<T>(
            this.httpClient,
            this.destroyRef,
            this.aBaseUrl,
            this.aPath,
            this.aPreFetch,
            new Sorting(this.aSortedFields),
            new Filters(this.aFilters),
        );

        return listQuery;
    }
}
